/** Help descriptions derived from the same client mirrors used by shops and portals. */
import { S, esc } from '../core/state.js';
import { ZONE_KO, zoneAt } from '../world/map.js';
import { LAIR_ENTRY, LAIR_FLOORS } from '../world/lair-map.js';
import { LIFE_STONE_SECS, LIFE_STONE_LEVELS } from '../world/cycle.js';
import { lore } from './panels/common.js';
import { PROPERTY_LISTINGS, PROPERTY_MIN_SALE_PRICE, PROPERTY_INVEST_STEP, SETTLEMENTS } from '../world/map.js';
import { NATIVE_MARKET_BUYBACK_PCT, NATIVE_SHARD_PRICE, NATIVE_BSTONE_PRICE, NATIVE_BOOK_PRICE, NATIVE_IMPRINT_PRICE } from './panels/native-village.js';
import { INVEST_AXIS_KO, INVEST_COUNT_KO } from './panels/realty.js';
import { withJosa } from '../world/josa.js';
import { vaultFateText } from './world-end-notice.js';

const money = (value) => `${Number(value).toLocaleString()}은화`;
/** 축 이름과 관형 수사(「네 투자」)는 투자 창의 축 표에서 읽는다(R163 — 도움말만 「세 투자」로 낡아 있었다). 수사 표는 realty.js 한 곳 */
const INVEST_AXES = Object.values(INVEST_AXIS_KO);
const INVEST_COUNT = INVEST_COUNT_KO;

/** 부동산·매입소·정착지·추천 스탯은 클라이언트 미러에서 읽어 도움말과 소식의 숫자를 맞춘다. */
export function settlementHelp() {
  const min = Math.max(PROPERTY_MIN_SALE_PRICE, Math.min(...PROPERTY_LISTINGS.map((p) => p.salePrice)));
  const max = Math.max(...PROPERTY_LISTINGS.map((p) => p.salePrice));
  const rentPct = Math.round(PROPERTY_LISTINGS[0].rentDaily / PROPERTY_LISTINGS[0].salePrice * 100);
  const squareCount = SETTLEMENTS.filter((s) => s.shape === 'square').length;
  return lore('부동산 · 집과 귀환', `
    · <b>메뉴 › 마을 › 부동산</b>의 「매매·임대 목록」에서 별장 매매·임대를 신청한다(신청은 정식 마을 안에서). 기본 매물은 ${PROPERTY_LISTINGS.length}채, 매입가는 ${money(min)}~${money(max)}, 하루 임대료는 매입가의 ${rentPct}%다. 현재 가격은 매물에서 확인한다.<br>
    · 최소 매입가는 ${money(PROPERTY_MIN_SALE_PRICE)}다. 집에는 ${withJosa(INVEST_AXES.join('·'), '을')} 투자하며 ${INVEST_COUNT} 투자 모두 상한이 없다. 기본 ${money(PROPERTY_INVEST_STEP)}마다 행운·회피는 +1%p, 올스탯은 각 기본 능력치 +1이 오르고, 무게 한도는 소지 무게가 오른다(한 번에 오르는 양은 부동산 투자 창에 나온다).<br>
    · 되팔기 가격은 <b>기준 건물값 + ${INVEST_COUNT} 투자액 합계의 70%</b>다. 주인이 바뀌어도 투자 내용은 건물에 남아 새 소유자에게 적용된다.<br>
    · <b>부동산 → 귀환 목적지</b>에서 마을이나 내 집을 고른다. 기본 목적지를 정하지 않았고 갈 곳이 둘 이상이면 귀환 주문서(R)를 쓸 때 선택한다. 초대를 수락한 거주자도 집으로 귀환할 수 있다.`)
    + lore('성문 밖 매입소', `
    · 왕도 성문 밖 매입소는 강화석·축복받은 강화석·서적·각인·짐꾼의 각인·날개·잡템·장비를 산다. 착용 중인 장비는 먼저 벗는다.<br>
    · 기본 매입가는 강화석 ${money(NATIVE_SHARD_PRICE)}, 축복받은 강화석 ${money(NATIVE_BSTONE_PRICE)}, 스킬북 ${money(NATIVE_BOOK_PRICE)}, 각인 ${money(NATIVE_IMPRINT_PRICE)}다.<br>
    · 일반 품목은 고블린 판매가의 ${NATIVE_MARKET_BUYBACK_PCT}%가 기준이다. 서적·각인·날개는 별도 가격, 명예 장비는 명예값으로 계산한다. 현재 판매 금액은 매입소에서 물품별로 확인한다.`)
    + lore('마을 이동과 스탯 자동 배분', `
    · 섬마을을 제외한 ${squareCount}곳은 사각형으로 조성된 정착지다. 성벽은 통과할 수 없으므로 성문으로 드나든다.<br>
    · <b>가방 → 능력 → 추천 스탯 자동 배분</b>을 켜면 직업별 공격·생존·보조 비율에 맞춰 남은 포인트를 전부 배분한다. 능력 화면에 내 직업의 비율을 표시한다. 이미 찍은 값은 줄이지 않고, 새 포인트는 목표보다 부족한 능력치부터 채운다. 직접 배분하려면 이 설정을 끈다.`);
}
export function expeditionHelp() {
  const stage = S.doom;
  const current = stage ? `현재 <b>${esc(stage.label || '세계 시계 확인')}</b> · 마지막 숨결 성문 <b>${S.keepOpen ? '개방' : '닫힘'}</b>.` : '세계 시계와 성문 상태는 접속 후 표시되는 안내를 확인한다.';
  return lore('드래곤 레어 · 층별 정복', `
    · <b>${esc(ZONE_KO[zoneAt(LAIR_ENTRY.x, LAIR_ENTRY.y)] || '상위 사냥터')}</b>의 드래곤 레어 입구로 들어간다. 표시 레벨(Lv${LAIR_ENTRY.level})은 <b>권장 난이도</b>이며 입장 레벨 제한이 아니다.<br>
    · ${LAIR_FLOORS.map((floor, i) => `${i + 1}층 <b>${esc(floor.name)}</b>`).join(' → ')} 순서다.<br>
    · 각 층의 <b>네임드를 모두 처치하면 보스가 등장</b>하고, 보스 처치가 확인되면 다음 문이 열린다. 마지막 보스를 쓰러뜨리면 정복 귀환문이 열린다.<br>
    · 네임드·보스 처치와 문 개방은 <b>같은 세계의 모든 플레이어가 공유</b>한다. <b>매일 자정(한국 시간)</b>에 봉인이 다시 걸려 모든 층의 네임드·보스가 돌아온다 — 그때 봉인된 층에 있던 사람은 레어 입구로 나온다. 세계가 바뀌어도 초기화된다.<br>
    · 레어는 <b>PK 가능</b>이며 일반 사망 손실이 적용된다. 신규 입장이 중단돼도 안에 있는 플레이어는 탐험을 계속하거나 밖으로 나갈 수 있다.`)
    + lore('세계의 끝 · 종말 드래곤과 마지막 숨결', `
    · ${current}<br>
    · <b>마지막 날 시작 전</b> 신전에 생명의 돌을 바치면 한 개당 <b>${LIFE_STONE_SECS / 3600}시간</b>을 늘린다. 누적 연장 상한은 없지만, <b>마지막 날부터 봉헌은 불가</b>하며 거절된 돌은 소비되지 않는다.<br>
    · 가방에서 생명의 돌을 선택해 <b>사용</b>하면 돌 1개가 사라지고 <b>내 레벨이 ${LIFE_STONE_LEVELS} 오른다</b>. 개인 사용은 마지막 날에도 가능하다.<br>
    · 마지막 날에는 지상 몬스터가 강해지고 종말 드래곤이 나타날 수 있다. 미니맵에서 드래곤의 위치·이동 경로와 브레스 예고를 확인한다.<br>
    · <b>마지막 숨결</b>은 마지막 날에 성문이 열리면 들어갈 수 있다. 성 안팎을 오갈 수 있으며, 종말 드래곤에게 쓰러지면 열린 성의 안뜰에서 부활한다. 사망 손실은 그대로 적용된다.<br>
    · 세계가 끝나면 캐릭터는 <b>이름 그대로 1레벨로 초기화</b>된다. ${vaultFateText(S.you)}. 무엇이 남고 무엇이 사라지는지는 「세계가 끝나면」 절(사망 절 바로 아래)에 있다.`);
  // R217(DESIGN 14.402) — 「세계가 끝나면」 표는 사망 절 바로 아래로 옮겼다(help-guide.js `worldEndHelp`). 원정 절 맨 뒤에만 있어 끝난 뒤에야 읽혔다
}
